import { useState } from 'react';
import type { FormEvent } from 'react';
import SectionHeading from './SectionHeading';

const FORM_ADDR = 'https://readdy.ai/api/form/daftbjbep053ijub1usg';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function TipForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('感谢你的投稿，我们会尽快与你联系。');
      form.reset();
      return;
    }
    fd.delete('contact_alt');

    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch(FORM_ADDR, { method: 'POST', body: fd });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const metaMsg = parsed?.meta?.message ?? parsed?.meta?.detail ?? '';
      const lower = `${parsed?.code ?? ''} ${metaMsg} ${text}`.toLowerCase();
      if (res.ok && parsed?.code === 'OK' && !lower.includes('spam')) {
        setStatus('success');
        setMessage('提交成功！感谢你的信任与支持，我们会尽快处理并与你联系。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(metaMsg && !lower.includes('spam') ? metaMsg : '提交失败，请稍后重试，或核对填写内容是否正确。');
      }
    } catch {
      setStatus('error');
      setMessage('网络似乎开小差了，请稍后重试。');
    }
  }

  return (
    <section id="contact" className="bg-background-100/60 py-14 md:py-20" aria-labelledby="contact-title">
      <div className="mx-auto max-w-[1320px] px-4 md:px-6">
        <SectionHeading
          eyebrow="Join & Tip"
          title="线索投稿 · 联系我们"
          desc="手上有新鲜瓜料、想表达观点，或对内容有疑问与建议？欢迎通过下方表单与我们交流，审核团队会在 24 小时内回应。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.5fr] lg:gap-10">
          <div className="flex flex-col justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 p-6 md:p-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-background-50/15 text-background-50">
              <i className="ri-customer-service-2-fill" style={{ width: 24, height: 24, fontSize: 24 }} />
            </span>
            <h3 className="mt-5 font-heading text-xl font-bold text-background-50 md:text-2xl">和我们聊聊</h3>
            <p className="mt-3 text-sm leading-relaxed text-background-50/85">
              你的每一条线索都可能在编辑确认后发布，每一次建议都在帮助吃瓜视界变得更好。我们珍视每一份来自用户的信任。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-background-50/90">
              <li className="flex items-center gap-2.5">
                <i className="ri-mail-line" style={{ width: 17, height: 17 }} />
                hello@chiguashijie.example
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-time-line" style={{ width: 17, height: 17 }} />
                编辑 7×24 在线，48 小时内回复
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-shield-check-line" style={{ width: 17, height: 17 }} />
                投稿内容一经采纳，将与你确认后发布
              </li>
            </ul>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-7"
            aria-label="线索投稿联系表单"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼</span>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="怎么称呼你"
                  className="w-full rounded-md border border-background-300 bg-background-100/50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">联系邮箱</span>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="用于接收回复"
                  className="w-full rounded-md border border-background-300 bg-background-100/50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">投稿类型</span>
                <select
                  name="topic"
                  required
                  defaultValue="线索投稿"
                  className="w-full rounded-md border border-background-300 bg-background-100/50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="线索投稿">线索投稿</option>
                  <option value="观点反馈">观点反馈</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他咨询">其他咨询</option>
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">详细内容</span>
                <textarea
                  name="content"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请尽量清晰地描述你的投稿或问题，方便我们快速理解与处理（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-100/50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>
            </div>

            <div className="gossip-extra-holder" aria-hidden="true">
              <label>
                备用联系方式
                <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" readOnly />
              </label>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin" style={{ width: 16, height: 16 }} />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2" style={{ width: 16, height: 16 }} />
                    提交投稿
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-500">提交即代表你同意我们依据内容规范使用素材，涉及个人隐私请谨慎填写。</p>
            </div>

            {status === 'success' && (
              <p role="status" className="mt-4 flex items-start gap-2 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800">
                <i className="ri-checkbox-circle-fill mt-0.5" style={{ width: 16, height: 16 }} />
                {message}
              </p>
            )}
            {status === 'error' && (
              <p role="alert" className="mt-4 flex items-start gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-fill mt-0.5" style={{ width: 16, height: 16 }} />
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}